import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createFavouriteApi,
  FAVOURITES_KEY,
  IFavouritesState,
  IStoredFavourites,
  MAX_TOP_FAVOURITES,
} from 'redux-state/favourites/types.js';
import { FavouriteLocationSummary } from 'types/markerTypes/location.favourites.js';
import { ILocationSummary } from 'types/markerTypesV1/marker-v1-view-type.js';
import { getLocalStorageItem, setLocalStorageItem } from 'utilities/helpers/local-storage.js';

const initialState: IFavouritesState = {
  topItems: [],
  bottomItems: [],
  allItems: [],
  favsAdded: false,
};

function saveFavouriteLocal(arr: FavouriteLocationSummary[], topCount: number) {
  setLocalStorageItem(FAVOURITES_KEY, {
    topCount,
    items: arr.map((item) => createFavouriteApi(item)),
  });
}

export const favouritesSlice = createSlice({
  name: 'favourites',
  initialState,
  reducers: {
    REDUX_FAVOURITES_ADD_ASYNC: (
      state,
      _: PayloadAction<{
        key: string;
      }>,
    ) => {
      /** Managed by saga */
      return state;
    },
    REDUX_FAVOURITES_LOAD_ASYNC: (state) => {
      /** Managed by saga */
      return state;
    },
    REDUX_FAVOURITES_LOAD_RESPONSE: (
      _,
      action: PayloadAction<{
        items: FavouriteLocationSummary[];
        topCount: number;
      }>,
    ) => {
      const { items, topCount } = action.payload;
      const topItems = items.slice(0, topCount);
      const bottomItems = items.slice(topCount);
      return {
        topItems,
        bottomItems,
        allItems: topItems.concat(bottomItems),
        favsAdded: false,
      };
    },

    REDUX_FAVOURITES_ADD_SUCCESS: (
      state,
      action: PayloadAction<{
        item: FavouriteLocationSummary;
        toTop: boolean;
      }>,
    ) => {
      const { item, toTop = false } = action.payload;
      // If the add fails then just return the original state and log an error
      if (item) {
        if (toTop) {
          const topItems = [...state.topItems, item];
          const bottomItems = state.bottomItems;
          const newState = {
            bottomItems,
            topItems,
            allItems: topItems.concat(bottomItems),
            favsAdded: true,
          };
          return newState;
        }
        const topItems = state.topItems;
        const bottomItems = state.bottomItems.concat(item);
        return {
          bottomItems,
          topItems,
          allItems: topItems.concat(bottomItems),
          favsAdded: true,
        };
      }
      console.error('Something went wrong with adding the favourite');
      return state;
    },
    REDUX_FAVOURITES_RESET_FAVS_ADDED: (state) => {
      return {
        ...state,
        favsAdded: false,
      };
    },
    REDUX_FAVOURITES_REMOVE: (
      state,
      action: PayloadAction<{
        key: string;
      }>,
    ) => {
      const { key } = action.payload;
      if (key) {
        const favourites: IStoredFavourites | undefined = getLocalStorageItem(FAVOURITES_KEY);
        if (favourites) {
          const idx = favourites.items.indexOf(key);
          if (idx > -1) {
            favourites.items.splice(idx, 1);
            favourites.topCount = Math.min(MAX_TOP_FAVOURITES, favourites.items.length);
            /** keep topCount the same so its replaced when one is removed */
            setLocalStorageItem(FAVOURITES_KEY, favourites);
          }
        }

        const matcher = (item: ILocationSummary) => createFavouriteApi(item) === key;

        let idx = state.topItems.findIndex(matcher);

        if (idx > -1) {
          if (state.bottomItems.length) {
            const topItems = state.topItems
              .slice(0, idx)
              .concat(state.topItems.slice(idx + 1).concat(state.bottomItems[0]));
            const bottomItems = [...state.bottomItems.slice(0, 0), ...state.bottomItems.slice(0 + 1)];
            return {
              topItems,
              bottomItems,
              allItems: topItems.concat(bottomItems),
              favsAdded: false,
            };
          }
          const topItems = state.topItems.slice(0, idx).concat(state.topItems.slice(idx + 1));
          const bottomItems = state.bottomItems;
          return {
            bottomItems,
            topItems,
            allItems: topItems.concat(bottomItems),
            favsAdded: false,
          };
        }

        idx = state.bottomItems.findIndex(matcher);

        if (idx < 0) {
          return state;
        }
        const topItems = state.topItems;
        const bottomItems = [...state.bottomItems.slice(0, idx), ...state.bottomItems.slice(idx + 1)];

        return {
          bottomItems,
          topItems,
          allItems: topItems.concat(bottomItems),
          favsAdded: false,
        };
      }
      return state;
    },
    REDUX_FAVOURITES_REORDER: (
      state,
      action: PayloadAction<{
        oldIndex: number;
        newIndex: number;
        newTopCount: number;
      }>,
    ) => {
      const combinedFavourites = [...state.topItems, ...state.bottomItems];
      const toMove = combinedFavourites.splice(action.payload.oldIndex, 1)[0];
      combinedFavourites.splice(action.payload.newIndex, 0, toMove);
      saveFavouriteLocal(combinedFavourites, action.payload.newTopCount);
      return {
        topItems: combinedFavourites.slice(0, action.payload.newTopCount),
        bottomItems: combinedFavourites.slice(action.payload.newTopCount),
        allItems: combinedFavourites,
        favsAdded: false,
      };
    },
    REDUX_FAVOURITES_ADD_ERROR: (
      state,
      _: PayloadAction<{
        key: string;
      }>,
    ) => {
      return state;
    },
  },
  selectors: {
    getAllFavourites: (sliceState) => {
      return sliceState.topItems.concat(sliceState.bottomItems);
    },
  },
});

export const {
  REDUX_FAVOURITES_LOAD_ASYNC,
  REDUX_FAVOURITES_LOAD_RESPONSE,
  REDUX_FAVOURITES_ADD_ASYNC,
  REDUX_FAVOURITES_ADD_SUCCESS,
  REDUX_FAVOURITES_ADD_ERROR,
  REDUX_FAVOURITES_REMOVE,
  REDUX_FAVOURITES_REORDER,
  REDUX_FAVOURITES_RESET_FAVS_ADDED,
} = favouritesSlice.actions;

export const favouritesReducer = favouritesSlice.reducer;
